"use client";

import { Core } from "@faire/design-tokens";
import { trackSlateDesignSystemUiButtonClickClick } from "@faire/web-api/events/slateDesignSystem/click/uiButtonClick";
import * as React from "react";
import styled, { css } from "styled-components";

import { Color, ColorOrKey } from "../../slate/Color";
import { SVGIcon } from "../../slate/icons/SVGIcon";
import { Spacer } from "../../slate/spacing";
import { themeOrDefault } from "../../slate/Theme";
import { ITypography, TypographyStyles } from "../Typography";

const animation = "all 0.2s ease-in-out";

export interface ILinkProps {
  children?: React.ReactNode | undefined;
  /**
   * @deprecated animated underlines are no longer in design system. Please remove animate.
   */
  animate?: boolean | "in" | "out";
  color?: ColorOrKey;
  hoverColor?: ColorOrKey;
  /**
   * If set, the given ID will be posted as a tracking event on click.
   * Note that this is just an ID, not the full event path.
   * @example 'signUp'
   */
  postEventButtonId?: string;
  typographyStyles?: ITypography;
}

export const LinkStyles = ({
  animate,
  color,
  hoverColor,
  disabled,
}: ILinkProps & ITextActionProps) =>
  css`
    color: ${color};
    ${disabled
      ? ""
      : css`
          cursor: pointer;
          &:hover {
            color: ${hoverColor ?? color ?? "inherit"};
          }
        `};
    ${animate
      ? css`
          transition: ${animation};
          position: relative;
          &:before,
          &:after {
            left: 0;
            content: "";
            position: absolute;
            bottom: 0;
            width: ${animate === "in" ? "0px" : "100%"};
            height: 1px;
            transition: ${animation};
            transition-duration: 0.25s;
            background: ${color ?? "currentColor"};
            opacity: ${animate === "in" ? "0" : "1"};
          }
          ${disabled
            ? ""
            : css`
                &:hover {
                  ${hoverColor
                    ? css`
                        color: ${hoverColor};
                      `
                    : ""}
                }
                &:hover:before,
                &:hover:after {
                  background: ${hoverColor ?? color ?? "currentColor"};
                  width: ${animate === "in" ? "100%" : "0px"};
                  opacity: ${animate === "in" ? "1" : "0"};
                  box-shadow: 0 0 1px 0 white inset;
                }
              `};
        `
      : css`
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-decoration-color: ${color ?? "currentColor"};

          ${disabled
            ? ""
            : css`
                &:active,
                &:hover,
                &:focus {
                  ${hoverColor
                    ? css`
                        text-decoration-color: ${hoverColor};
                      `
                    : ""}
                }
              `};
        `};
  `;

const getAnimation = (
  animate?: boolean | "in" | "out"
): "in" | "out" | false => {
  if (animate === true) {
    // default to 'in'
    return "in";
  } else if (typeof animate === "string") {
    return animate;
  } else {
    return false;
  }
};

const hoverColorOrDefault = (color: ColorOrKey) =>
  Color[color as keyof typeof Color] || color;

const IconWrapper = styled.div<{ $disabled?: boolean }>`
  font-size: 16px;
  height: ${(props) => props.theme?.fonts?.sansSerif?.p?.lineHeight ?? "20px"};
  display: flex;
  align-items: center;

  color: ${({ $disabled, color, theme }) => {
    const { buttons } = themeOrDefault(theme);
    if (color === "primary" || color === "secondary") {
      color = Core.text.default;
    }
    return $disabled
      ? Core.text.subdued
      : color ?? buttons.secondary.text.color;
  }};
`;

interface WrapperStyles {
  $color: string | undefined;
  $typographyStyles?: ITypography | undefined;
  $disabled: boolean | undefined;
  $hoverColor: string | undefined;
  $animate: boolean | "in" | "out" | undefined;
}

const TextActionChildrenWrapper = styled.span<WrapperStyles>`
  ${(props) =>
    TypographyStyles(props.$typographyStyles ?? { p: true }, props.theme)}
  ${({ $animate, $color, $hoverColor, $disabled }) =>
    LinkStyles({
      animate: getAnimation($animate),
      hoverColor: hoverColorOrDefault(
        $hoverColor ?? $color ?? Core.text.default
      ),
      disabled: $disabled,
    })};
  color: ${({ $disabled, $color, theme }) => {
    const { buttons } = themeOrDefault(theme);
    return $disabled
      ? Core.text.subdued
      : $color ?? buttons.secondary.text.color;
  }};
`;

const SpanRow = styled.span`
  display: flex;
  white-space: nowrap;
`;

export const TextAction: React.FC<ITextActionProps & ILinkProps> = ({
  children,
  icon,
  ...props
}) => {
  return (
    <SpanRow>
      <TextActionChildrenWrapper
        $animate={props.animate}
        $color={props.color}
        $hoverColor={props.hoverColor}
        $disabled={props.disabled}
        $typographyStyles={props.typographyStyles}
        data-test-id="tertiary-text"
      >
        {children}
      </TextActionChildrenWrapper>
      {icon ? (
        <>
          <Spacer size={4} />
          <IconWrapper $disabled={props.disabled} color={props.color}>
            {icon}
          </IconWrapper>
        </>
      ) : null}
    </SpanRow>
  );
};

export interface ITextActionProps {
  children?: React.ReactNode | undefined;
  disabled?: boolean;
  icon?: React.ReactElement<
    React.MemoExoticComponent<
      React.ForwardRefExoticComponent<SVGIcon.IProps & SVGElement>
    >
  >;
  className?: string;
}

export const onClickTextAction =
  <T,>(
    onClick?: ((e: React.MouseEvent<T, MouseEvent>) => void) | (() => void),
    postEventButtonId?: string
  ) =>
  (e: React.MouseEvent<T, MouseEvent>) => {
    if (postEventButtonId) {
      trackSlateDesignSystemUiButtonClickClick(postEventButtonId);
    }
    onClick?.(e);
  };
